import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";
import { api } from "../../lib/api";
import { handleError } from "../../lib/errors";
import { createLogger } from "../../lib/logger";
import { notify } from "../../lib/notify";
import { useAppStore } from "../../store/useAppStore";
import { Deployment, Service } from "../../types";
import { DeploymentDrawer } from "../deployments/DeploymentDrawer";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "../ui/breadcrumb";
import { Button } from "../ui/button";
import { ErrorState } from "../ui/error-state";
import { Skeleton, SkeletonCard } from "../ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { MembersTab } from "./MembersTab";
import {
  useProjectDetails,
  useProjectServices,
  useProjectDeployments,
  useDeploymentLogs,
} from "./project-details";
import {
  ProjectDetailsHeader,
  ProjectOverviewTab,
  DeploymentDetailsModal,
} from "./project-details";

const log = createLogger("ProjectDetailsPage");

interface ProjectDetailsPageProps {
  projectId: string;
}

export function ProjectDetailsPage({ projectId }: ProjectDetailsPageProps) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { removeProject } = useAppStore();

  // Local state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [selectedEnvironment, setSelectedEnvironment] = useState<any>(null);
  // true while we're resolving an environmentId from the URL — prevents the
  // "Select an environment" empty state from flashing before the fetch resolves.
  const [environmentLoading, setEnvironmentLoading] = useState(
    () => !!new URLSearchParams(window.location.search).get("environmentId")
  );
  const handleAddService = useCallback(() => {
    const envParam = selectedEnvironment?.id
      ? `?environment=${selectedEnvironment.id}`
      : "";
    navigate(`/projects/${projectId}/services/new${envParam}`);
  }, [navigate, projectId, selectedEnvironment]);
  const [selectedDeployment, setSelectedDeployment] =
    useState<Deployment | null>(null);
  const [deploymentDrawerOpen, setDeploymentDrawerOpen] = useState(false);
  // Read-only for now: the id is consumed by <DeploymentDrawer> below but is not
  // set anywhere yet, so we omit the unused setter to keep the lint clean.
  const [deploymentDrawerId] = useState<string>("");

  // Custom hooks
  const {
    project,
    loading: projectLoading,
    error: projectError,
    refetch: refetchProject,
  } = useProjectDetails(projectId);
  useDocumentTitle(project?.name);
  // Pass "" as projectId while the environment is still resolving from the URL.
  // The hooks treat "" as disabled (no fetch, clears state) so we avoid a
  // spurious unfiltered fetch and the transition-frame flash that would follow.
  const activeProjectId = environmentLoading ? "" : projectId;
  const { services, loading: servicesLoading, refetch: refetchServices } = useProjectServices(
    activeProjectId,
    selectedEnvironment?.id
  );
  const { deployments, loading: deploymentsLoading, refetch: refetchDeployments } = useProjectDeployments(
    activeProjectId,
    selectedEnvironment?.id
  );
  const { logs: deploymentLogs, viewLogs } = useDeploymentLogs();

  const confirmDelete = useCallback(async () => {
    if (!project) return;
    setDeleting(true);
    try {
      await api.projects.delete(projectId);
      removeProject(projectId);
      notify.success("Project deleted", { description: project.name });
      navigate("/projects");
    } catch (error) {
      handleError(error, { scope: "ProjectDetailsPage.delete", context: { projectId } });
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  }, [project, projectId, removeProject, navigate]);

  // Calculate environment analytics
  const failedDeployments = deployments.filter(
    (d: Deployment) => d.status === "failed"
  );
  // Deployments arrive newest-first; sort defensively so the stat card links
  // to the most recent failure even if the API ordering changes.
  const latestFailed = [...failedDeployments].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  )[0];
  const environmentAnalytics = {
    servicesCount: services.length,
    runningServices: services.filter((s: Service) => s.status === "running")
      .length,
    deploymentsCount: deployments.length,
    failedDeployments: failedDeployments.length,
    failedDeploymentLink: latestFailed
      ? `/projects/${projectId}/deployments/${latestFailed.id}`
      : undefined,
  };

  // Handle environment change
  const handleEnvironmentChange = useCallback(
    (environment: any) => {
      setSelectedEnvironment(environment);
      log.debug("Environment changed to", environment);

      // Update only the environmentId param, preserving the active tab in the URL.
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (environment?.id) next.set("environmentId", environment.id);
        else next.delete("environmentId");
        return next;
      });
    },
    [setSearchParams]
  );


  // Stable string dep: avoids re-running fetchEnvironments on every env switch
  // (handleEnvironmentChange calls setSearchParams which would otherwise retrigger).
  const environmentIdFromUrl = searchParams.get("environmentId");

  // Handle direct navigation with environment ID in URL
  useEffect(() => {
    if (!environmentIdFromUrl) {
      // No env ID in URL — nothing to resolve.
      setEnvironmentLoading(false);
      return;
    }

    // Have an env ID but project hasn't loaded yet — keep loading=true and wait.
    if (!project) return;

    const fetchEnvironments = async () => {
      try {
        const data = await api.environments.listByProject(projectId);
        const environment = data.environments?.find(
          (env: any) => env.id === environmentIdFromUrl
        );
        if (environment) {
          setSelectedEnvironment(environment);
          log.debug("Auto-selected environment from URL", environment);
        }
      } catch (error) {
        log.error("Failed to fetch environments for URL param", error);
      } finally {
        setEnvironmentLoading(false);
      }
    };

    fetchEnvironments();
  }, [project, projectId, environmentIdFromUrl]);

  // Loading state — skeletons matching the header + analytics + list layout
  // (consistent with the rest of the app; avoids a bare "Loading…" flash).
  if (projectLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-4 w-40" />
        <div className="space-y-2">
          <Skeleton className="h-9 w-64" />
          <Skeleton className="h-4 w-80" />
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <SkeletonCard />
          <SkeletonCard />
        </div>
        <SkeletonCard />
      </div>
    );
  }

  // Error state — use the shared ErrorState (friendly message + retry), with a
  // secondary path back to the projects list.
  if (projectError || !project) {
    return (
      <div className="space-y-4">
        <ErrorState
          title="Couldn't load this project"
          description={projectError || "Project not found"}
          onRetry={refetchProject}
        />
        <div className="flex justify-center">
          <Button onClick={() => navigate("/projects")} variant="outline">
            Back to Projects
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb: Projects > current project. The link navigates via the
          router (no full reload) while keeping the href for middle/ctrl-click. */}
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/projects">Projects</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>{project.name}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      {/* Project Header */}
      <ProjectDetailsHeader
        project={project}
        onEnvironmentChange={handleEnvironmentChange}
        onProjectUpdated={refetchProject}
        onDeleteClick={() => setDeleteDialogOpen(true)}
      />

      {/* Tabbed content: Overview + Members.
          The active tab is persisted in the URL (?tab=members) so deep links and
          browser back/forward work correctly without reloading the page. */}
      <Tabs
        value={searchParams.get("tab") ?? "overview"}
        onValueChange={(tab) =>
          setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            if (tab === "overview") next.delete("tab");
            else next.set("tab", tab);
            return next;
          })
        }
      >
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="members">Members</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6 mt-4">
          <ProjectOverviewTab
            services={services}
            servicesLoading={servicesLoading || environmentLoading}
            deploymentsLoading={deploymentsLoading}
            selectedEnvironment={environmentLoading ? null : selectedEnvironment}
            environmentAnalytics={environmentAnalytics}
            onAddService={handleAddService}
            onRefreshServices={refetchServices}
            onDeploymentStarted={refetchDeployments}
          />
        </TabsContent>

        <TabsContent value="members" className="mt-4">
          <MembersTab project={project} />
        </TabsContent>
      </Tabs>


      {/* Deployment Drawer */}
      <DeploymentDrawer
        open={deploymentDrawerOpen}
        onOpenChange={setDeploymentDrawerOpen}
        deploymentId={deploymentDrawerId}
      />

      {/* Deployment Details Modal */}
      <DeploymentDetailsModal
        deployment={selectedDeployment}
        logs={deploymentLogs}
        onClose={() => setSelectedDeployment(null)}
        onLoadLogs={viewLogs}
      />

      {/* Delete project confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{project.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the project and all its environments,
              services, and deployments. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleting}
              onClick={(e) => {
                e.preventDefault();
                confirmDelete();
              }}
            >
              {deleting ? "Deleting…" : "Delete project"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
